import {
  Component,
  effect,
  inject,
  OnInit,
  signal,
  Signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { CheckPoint } from '../../core/models/checkPoint.model';
import { EntitlementsCache } from '../../core/models/entitlements.model';
import { Form } from '../../core/models/form.model';
import { AssetInstanceService } from '../../core/services/asset-instance.service';
import { CheckPointService } from '../../core/services/checkPoint.service';
import { EntitlementsService } from '../../core/services/entitlements.service';
import { FormService } from '../../core/services/form.service';
import { LoggerService } from '../../core/services/logger.service';
import { ModalConfig, ModalService } from '../../core/services/modal.service';
import { TranslationService } from '../../core/services/translation.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { EntityListSelectorComponent } from '../../shared/components/entity-list-selector/entity-list-selector.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { UpsellDialogComponent } from '../../shared/components/upsell-dialog/upsell-dialog.component';
import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { TippyDirective } from '../../shared/directives/tippy.directive';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-checkPoints',
  imports: [
    RouterLink,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    TippyDirective,
    HasPermissionDirective,
    IconComponent,
    ButtonComponent,
    EntityListSelectorComponent,
    TranslatePipe,
  ],
  templateUrl: './checkPoints.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './checkPoints.component.css',
})
export class CheckPointsComponent implements OnInit {
  private checkPointService = inject(CheckPointService);
  private assetInstanceService = inject(AssetInstanceService);
  private formService = inject(FormService);
  private entitlementsService = inject(EntitlementsService);
  private modalService = inject(ModalService);
  private translationService = inject(TranslationService);
  private router = inject(Router);

  // Reactive signals from queries - auto-update on data changes
  checkPoints!: Signal<CheckPoint[]>;
  loading!: Signal<boolean>;
  error!: Signal<string | null>;
  forms!: Signal<any[]>;
  formLoading!: Signal<boolean>;
  formError!: Signal<string | null>;
  entitlements!: Signal<EntitlementsCache | null>;
  // Map of checkpoint ID to its forms
  checkPointForms = signal<Map<string, Form[]>>(new Map());
  // Map of checkpoint ID to active instance count
  instanceCounts = signal<Map<string, number>>(new Map());
  private logger = inject(LoggerService).getLogger('checkpoints');

  constructor() {
    // Watch for checkpoints changes and reload forms
    effect(() => {
      const checkPoints = this.checkPoints?.();
      if (checkPoints) {
        this.logger.debug('checkpoints loaded:', checkPoints.length, checkPoints);
      }
      if (checkPoints && checkPoints.length > 0 && !this.loading?.()) {
        this.loadCheckPointForms();
        this.loadInstanceCounts();
      }
    });
  }

  async ngOnInit() {
    // Create reactive queries - they auto-refresh on sync and local changes
    const checkPointQuery = this.checkPointService.createQuery();
    this.checkPoints = checkPointQuery.data;
    this.loading = checkPointQuery.loading;
    this.error = checkPointQuery.error;

    const typeQuery = this.formService.createQuery();
    this.forms = typeQuery.data;
    this.formLoading = typeQuery.loading;
    this.formError = typeQuery.error;

    const entitlementsQuery = this.entitlementsService.createQuery();
    this.entitlements = entitlementsQuery.data;
  }

  /**
   * Gates the "New Checkpoint" action against the plan's asset hard cap.
   * Save-time re-check lives in CheckPointFormComponent.doSave() as defense
   * in depth (e.g. a direct navigation to /checkpoints/new).
   */
  onNewCheckpointClick(): void {
    const entitlements = this.entitlements?.();
    // Sample-scenario checkpoints don't count toward the plan limit (see CheckPointService.count).
    const currentCount = (this.checkPoints?.() ?? []).filter((c) => !c.sample_scenario).length;

    if (
      entitlements &&
      this.entitlementsService.limitReached(currentCount, entitlements.asset_limit)
    ) {
      this.showAssetLimitUpsell(entitlements.plan_name);
      return;
    }

    this.router.navigate(['/checkpoints/new']);
  }

  private async showAssetLimitUpsell(planName: string): Promise<void> {
    const closeSignal = signal<{ action: string } | null>(null);

    const config: ModalConfig = {
      component: UpsellDialogComponent,
      componentInputs: { limitType: 'asset', planName },
      title: this.translationService.translate('upsellDialog.title'),
      size: 'small',
      closeSignal,
      actionButtons: [
        {
          id: 'cancel',
          label: this.translationService.translate('upsellDialog.cancel'),
          variant: 'text',
          onClick: async () => closeSignal.set({ action: 'cancel' }),
        },
        {
          id: 'upgrade',
          label: this.translationService.translate('upsellDialog.upgradePlan'),
          variant: 'primary',
          onClick: async () => closeSignal.set({ action: 'upgrade' }),
        },
      ],
    };

    const result = await this.modalService.openModal(config);
    if (result?.action === 'upgrade') {
      this.router.navigate(['/settings/billing']);
    }
  }

  async loadCheckPointForms() {
    const checkPoints = this.checkPoints();
    const typesMap = new Map<string, Form[]>();

    for (const checkPoint of checkPoints) {
      if (checkPoint.id) {
        try {
          const types = await this.formService.getByCheckPointId(checkPoint.id);
          typesMap.set(checkPoint.id, types);
        } catch (err) {
          this.logger.error(`Failed to load forms for checkpoint ${checkPoint.id}:`, err);
        }
      }
    }

    this.checkPointForms.set(typesMap);
  }

  showCheckPointQrCode(checkpoint: CheckPoint, event: Event) {
    event.stopPropagation();
    this.router.navigate(['/checkpoints', checkpoint.id]);
  }

  showFormQrCode(checkpoint: CheckPoint, interactionType: Form, event: Event) {
    event.stopPropagation();
    this.router.navigate(['/checkpoints', checkpoint.id, interactionType.id, 'view']);
  }

  showFormView(checkpoint: CheckPoint, interactionType: Form, event: Event) {
    event.stopPropagation();
    this.router.navigate(['/checkpoints', checkpoint.id, interactionType.id, 'view']);
  }

  editCheckPoint(checkPoint: CheckPoint) {
    this.router.navigate(['/checkpoints', checkPoint.id]);
  }

  async loadInstanceCounts() {
    const checkPoints = this.checkPoints();
    const countsMap = new Map<string, number>();

    for (const checkPoint of checkPoints) {
      if (checkPoint.id) {
        try {
          const count = await this.assetInstanceService.getCountByCheckpointId(checkPoint.id);
          if (count > 0) {
            countsMap.set(checkPoint.id, count);
          }
        } catch (err) {
          this.logger.error(`Failed to load instance count for checkpoint ${checkPoint.id}:`, err);
        }
      }
    }

    this.instanceCounts.set(countsMap);
  }

  getInstanceCount(checkpointId: string | undefined): number {
    if (!checkpointId) return 0;
    return this.instanceCounts().get(checkpointId) || 0;
  }

  getCheckPointForms(checkpointId: string | undefined): Form[] {
    if (!checkpointId) return [];
    return this.checkPointForms().get(checkpointId) || [];
  }

  get debugInfo(): string {
    return (
      `forms().length: ${this.forms().length}\n` +
      `forms(): ${JSON.stringify(this.forms())}\n` +
      `checkPoints().length: ${this.checkPoints().length}\n` +
      `checkPoints(): ${JSON.stringify(this.checkPoints())}`
    );
  }
}
